<template>
  <AdminLayout>
    <template #topbar-search>
      <div class="flex flex-column">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by code..." size="small" />
        </IconField>
      </div>
    </template>

    <template #header>
      <Breadcrumb :items="[{ label: 'Discounts' }]" />
      <Button label="New Discount" icon="pi pi-plus" class="accent-button" @click="openCreateDialog" />
    </template>

    <!-- Discounts Table -->
    <div class="container">
      <DiscountList
        :tableData="filteredDiscounts"
        :isLoading="isLoading"
        :totalDiscounts="discounts.length"
        @edit="openEditDialog"
        @toggle="openToggleDialog"
      />
    </div>

    <!-- Create / Edit Dialog -->
    <DiscountFormDialog
      v-model:visible="showFormDialog"
      :editingDiscount="editingDiscount"
      :isSubmitting="isSubmitting"
      @submit="submitForm"
    />

    <!-- Toggle Confirm Dialog -->
    <Dialog
      v-model:visible="showToggleDialog"
      :header="toggleTarget?.isActive ? 'Deactivate Discount' : 'Activate Discount'"
      modal
      style="width: 380px;"
    >
      <p>
        Are you sure you want to {{ toggleTarget?.isActive ? 'deactivate' : 'activate' }} the
        discount code <strong>{{ toggleTarget?.code }}</strong>?
      </p>
      <template #footer>
        <Button label="Cancel" severity="secondary" @click="showToggleDialog = false" />
        <Button
          :label="toggleTarget?.isActive ? 'Deactivate' : 'Activate'"
          :severity="toggleTarget?.isActive ? 'danger' : 'success'"
          @click="confirmToggle"
          :loading="isSubmitting"
        />
      </template>
    </Dialog>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { getAllDiscounts, createDiscount, updateDiscount, toggleDiscount } from "@/api/admin";
import DiscountList from "@/components/dashboard/admin/discounts/discountList.vue";
import DiscountFormDialog from "@/components/dashboard/admin/discounts/discountFormDialog.vue";

const toast = useToast();
const discounts = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const searchValue = ref("");
const showFormDialog = ref(false);
const showToggleDialog = ref(false);
const editingDiscount = ref(null);
const toggleTarget = ref(null);

const filteredDiscounts = computed(() => {
  if (!searchValue.value.trim()) return discounts.value;
  return discounts.value.filter(d =>
    d.code.toLowerCase().includes(searchValue.value.toLowerCase())
  );
});

const fetchDiscounts = async () => {
  try {
    isLoading.value = true;
    const res = await getAllDiscounts();
    discounts.value = res.data;
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to load discounts.", life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchDiscounts);

const openCreateDialog = () => {
  editingDiscount.value = null;
  showFormDialog.value = true;
};

const openEditDialog = (discount) => {
  editingDiscount.value = discount;
  showFormDialog.value = true;
};

const openToggleDialog = (discount) => {
  toggleTarget.value = discount;
  showToggleDialog.value = true;
};

const submitForm = async (payload) => {
  try {
    isSubmitting.value = true;
    if (editingDiscount.value) {
      await updateDiscount(editingDiscount.value._id, payload);
      toast.add({ severity: "success", summary: "Updated", detail: "Discount updated.", life: 3000 });
    } else {
      await createDiscount(payload);
      toast.add({ severity: "success", summary: "Created", detail: "Discount created.", life: 3000 });
    }
    showFormDialog.value = false;
    fetchDiscounts();
  } catch (err) {
    const msg = err.response?.data?.message || "Something went wrong.";
    toast.add({ severity: "error", summary: "Error", detail: msg, life: 4000 });
  } finally {
    isSubmitting.value = false;
  }
};

const confirmToggle = async () => {
  try {
    isSubmitting.value = true;
    const res = await toggleDiscount(toggleTarget.value._id);
    toast.add({ severity: "success", summary: "Updated", detail: res.data.message, life: 3000 });
    showToggleDialog.value = false;
    fetchDiscounts();
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to toggle discount.", life: 3000 });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.container {
  width: 100%;
  border-radius: 8px;
}
</style>
